import { Day } from "PFApp/booking/parts/overview/booking_module_calendar/booking_calendar/booking_calendar_store";
import { BookingCalendarContext } from "PFApp/booking/parts/overview/booking_module_calendar/booking_calendar/booking_calendar_store/booking_calendar_store_context";
import { calculateBarDimensions } from "PFApp/booking/parts/overview/calendar/bars/bars.utils";
import { useBookingModuleStoreActions, useCalendarRoot, useCalendarRootActions } from "PFApp/booking/store";
import { useBookingPastDaysLimit } from "PFApp/hooks";
import { useContext, useRef } from "react";

import { DnDMode } from "../../../bars/booking_bar/use_booking_bar_dnd";
import {
  getMaxVerticalTop,
  getNumberOfDraggedDays,
  getUpdatedDates,
  updateElementPosition
} from "../drag_calculations";
import { useBookingBarElement } from "../use_booking_bar_element";
import type { DragAxes } from "../use_drag_axes";

const SOURCE_ROW_Z_INDEX = "2";

type DragStartOrigin = {
  originX: number;
  originY: number;
};

type OriginalBarStyles = {
  left: string;
  width: string;
  top: string;
};

type DragSnapshot = {
  days: Day[];
  sourceRowRectX: number;
  originClickX: number;
  originClickY: number;
  maxTopUp: number;
  maxTopDown: number;
  originalBarStyles: OriginalBarStyles;
  sourceRowElement: HTMLElement | null;
  originalSourceRowZIndex: string;
  lastDraggedDays: number;
  barDimensions: { left: string; width: string } | null;
};

export type UseBookingBarManagerReturn = {
  onDragStart: (origin: DragStartOrigin) => void;
  onDragMove: (event: PointerEvent, axes: DragAxes) => void;
  onDragEnd: () => void;
  onDragCancel: () => void;
};

export const useBookingBarManager = (): UseBookingBarManagerReturn => {
  const calendarRoot = useCalendarRoot();
  const { getRowsWithRefs } = useCalendarRootActions(calendarRoot);
  const { getBookingDragAndDropData, setBookingDragAndDropData, getBookingData } =
    useBookingModuleStoreActions();
  const { getBookingBarElement } = useBookingBarElement();
  const pastDaysLimit = useBookingPastDaysLimit();
  const calendarStoreApi = useContext(BookingCalendarContext);

  const snapshotRef = useRef<DragSnapshot | null>(null);

  const onDragStart = ({ originX, originY }: DragStartOrigin): void => {
    const { bookingId, sourceRow } = getBookingDragAndDropData();
    const bookingBarElement = getBookingBarElement();

    if (!bookingId || !sourceRow || !bookingBarElement) {
      return;
    }

    const rowsWithRefs = getRowsWithRefs();
    const sourceRowRef = rowsWithRefs.get(sourceRow.key)?.rowRef ?? null;

    const sourceRowRectX = sourceRowRef?.getBoundingClientRect().x ?? 0;
    const originClickX = originX - sourceRowRectX;

    const { maxTopUp, maxTopDown } = getMaxVerticalTop({
      rowsWithRefs,
      initialClientPositionY: originY
    });

    const days = calendarStoreApi?.getState().days ?? [];

    snapshotRef.current = {
      days,
      sourceRowRectX,
      originClickX,
      originClickY: originY,
      maxTopUp,
      maxTopDown,
      sourceRowElement: sourceRowRef,
      originalSourceRowZIndex: sourceRowRef?.style.zIndex ?? "",
      originalBarStyles: {
        left: bookingBarElement.style.left,
        width: bookingBarElement.style.width,
        top: bookingBarElement.style.top
      },
      lastDraggedDays: 0,
      barDimensions: null
    };

    sourceRowRef?.style.setProperty("z-index", SOURCE_ROW_Z_INDEX);

    const booking = getBookingData(bookingId)?.booking;
    if (booking) {
      setBookingDragAndDropData({
        draggingStartDate: booking.start_date,
        draggingEndDate: booking.end_date
      });
    }
  };

  const onDragMove = (event: PointerEvent, axes: DragAxes): void => {
    const snapshot = snapshotRef.current;
    const { draggingMode, bookingId } = getBookingDragAndDropData();
    const bookingBarElement = getBookingBarElement();

    if (!snapshot || !draggingMode || !bookingId || !bookingBarElement) {
      return;
    }

    const booking = getBookingData(bookingId)?.booking;
    if (!booking) {
      return;
    }

    const { days, sourceRowRectX, originClickX, originClickY, maxTopUp, maxTopDown } = snapshot;

    const draggedDaysNumber = getNumberOfDraggedDays({
      currentMouseX: event.clientX,
      originClickX,
      rowStartX: sourceRowRectX,
      days
    });

    if (axes.horizontal && draggedDaysNumber !== snapshot.lastDraggedDays) {
      const { startDate, endDate } = getUpdatedDates(
        draggingMode,
        draggedDaysNumber,
        booking.start_date,
        booking.end_date,
        days,
        pastDaysLimit
      );

      const { left, width } = calculateBarDimensions(startDate.toISOString(), endDate.toISOString(), days);

      if (left !== null && width !== null) {
        snapshot.barDimensions = { left: `${left}px`, width: `${width}px` };
      }

      setBookingDragAndDropData({
        draggingStartDate: startDate.toISOString(),
        draggingEndDate: endDate.toISOString()
      });

      snapshot.lastDraggedDays = draggedDaysNumber;
    }

    if (snapshot.barDimensions) {
      updateElementPosition(bookingBarElement, snapshot.barDimensions);
    }

    if (draggingMode === DnDMode.HorizontalVertical) {
      const rawTop = axes.vertical ? event.clientY - originClickY : 0;
      const top = Math.min(maxTopDown, Math.max(maxTopUp, rawTop));
      updateElementPosition(bookingBarElement, { top: `${top}px` });
    }
  };

  const onDragEnd = (): void => {
    const snapshot = snapshotRef.current;
    snapshot?.sourceRowElement?.style.setProperty("z-index", snapshot.originalSourceRowZIndex);

    setBookingDragAndDropData({
      draggingStartDate: null,
      draggingEndDate: null
    });

    snapshotRef.current = null;
  };

  const onDragCancel = (): void => {
    const snapshot = snapshotRef.current;
    const bookingBarElement = getBookingBarElement();

    if (snapshot) {
      updateElementPosition(bookingBarElement, snapshot.originalBarStyles);
    }

    onDragEnd();
  };

  return { onDragStart, onDragMove, onDragEnd, onDragCancel };
};
